import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Save, Loader2, Coins, ClipboardList, Plus, Trash2, ToggleLeft } from "lucide-react";
import { customCreditUnitPriceFromPackages } from "@/lib/customCreditUnitPrice";
import {
  normalizeServiceAvailability,
  SERVICE_AVAILABILITY_KEYS,
  SERVICE_AVAILABILITY_LABELS,
} from "@/lib/serviceAvailability";

export function AdminPricingConfig() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/pricing-config");
      const data = { ...r.data };
      if (!data.seller_services_page) {
        data.seller_services_page = {
          hero_title: "",
          hero_lead: "",
          cta_primary_label: "",
          cta_primary_path: "",
          cta_secondary_label: "",
          cta_secondary_path: "",
          sections: [],
        };
      }
      data.service_availability = normalizeServiceAvailability(data.service_availability);
      setCfg(data);
    } catch (e) {
      const status = e.response?.status;
      toast.error(
        status === 404
          ? "API nem elérhető (404). Indítsd a backendet (pl. :8000) és ellenőrizd a REACT_APP_BACKEND_URL-t."
          : e.response?.data?.detail || "Árazás betöltése sikertelen",
      );
      setCfg(null);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        packages: cfg.packages.map((p) => ({ credits: Number(p.credits), price: Number(p.price) })),
        listing_upload: Number(cfg.listing_upload),
        listing_daily: Number(cfg.listing_daily),
        detailed_stats_daily: Number(cfg.detailed_stats_daily),
        detailed_stats_guarantee_credits: Number(cfg.detailed_stats_guarantee_credits ?? 60),
        detailed_stats_guarantee_days: Number(cfg.detailed_stats_guarantee_days ?? 14),
        detailed_stats_guarantee_max_views: Number(cfg.detailed_stats_guarantee_max_views ?? 10),
        credit_subscription_annual_discount_percent: Number(cfg.credit_subscription_annual_discount_percent ?? 20),
        dealer_credit_unit_price: Number(cfg.dealer_credit_unit_price ?? 7),
        addons_one_time: Object.fromEntries(Object.entries(cfg.addons_one_time).map(([k, v]) => [k, Number(v)])),
        addons_weekly: Object.fromEntries(Object.entries(cfg.addons_weekly).map(([k, v]) => [k, Number(v)])),
        rewards: Object.fromEntries(Object.entries(cfg.rewards).map(([k, v]) => [k, Number(v)])),
        service_availability: cfg.service_availability,
        listing_vin_required: !!cfg.listing_vin_required,
        listing_requirements: (cfg.listing_requirements || []).map((r) => ({
          id: r.id || `req_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          text: String(r.text || "").trim(),
          required: !!r.required,
        })).filter((r) => r.text),
      };
      await api.patch("/admin/pricing-config", payload);
      toast.success("Árazás mentve");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mentés sikertelen");
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) {
    return (
      <div className="text-slate-400 text-sm space-y-2" data-testid="admin-pricing-load-error">
        <p>Nem sikerült betölteni az árazást.</p>
        <button type="button" onClick={load} className="text-electric hover:underline text-sm">
          Újrapróbálás
        </button>
      </div>
    );
  }

  const setPkg = (i, k, v) => {
    const packages = [...cfg.packages];
    packages[i] = { ...packages[i], [k]: v };
    setCfg({ ...cfg, packages });
  };

  const addPkg = () => setCfg({ ...cfg, packages: [...cfg.packages, { credits: 100, price: 1000 }] });
  const removePkg = (i) => setCfg({ ...cfg, packages: cfg.packages.filter((_, x) => x !== i) });

  return (
    <div data-testid="admin-pricing-config" className="space-y-8">
      <div className="glass rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Coins className="w-4 h-4 text-electric" strokeWidth={1.5} />
          <h3 className="font-heading text-lg">Kreditcsomagok</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {cfg.packages.map((p, i) => (
            <div key={i} className="flex items-center gap-2 bg-white/[0.03] rounded-lg p-2" data-testid={`admin-pkg-row-${i}`}>
              <NumInput testid={`admin-pkg-credits-${i}`} label="Kredit" value={p.credits} onChange={(v) => setPkg(i, "credits", v)} />
              <NumInput testid={`admin-pkg-price-${i}`} label="Ár (Ft)" value={p.price} onChange={(v) => setPkg(i, "price", v)} />
              <button data-testid={`admin-pkg-remove-${i}`} onClick={() => removePkg(i)} className="px-2 py-1 text-xs rounded bg-red-500/10 text-red-400 hover:bg-red-500/20">Törlés</button>
            </div>
          ))}
        </div>
        <button data-testid="admin-pkg-add" onClick={addPkg} className="mt-3 px-3 py-1.5 text-xs rounded-full glass hover:bg-white/10">+ Új csomag</button>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
          <NumBox
            testid="admin-annual-sub-discount"
            label="Éves előfizetés kedvezmény"
            suffix="% (12× havi díjból)"
            value={cfg.credit_subscription_annual_discount_percent ?? 20}
            onChange={(v) => setCfg({ ...cfg, credit_subscription_annual_discount_percent: v })}
          />
          <NumBox
            testid="admin-dealer-credit-unit"
            label="Kereskedői csomag"
            suffix="Ft / kredit (havi előfiz.)"
            value={cfg.dealer_credit_unit_price ?? 7}
            onChange={(v) => setCfg({ ...cfg, dealer_credit_unit_price: v })}
          />
        </div>
        <p className="text-xs text-slate-500 mt-4" data-testid="admin-custom-unit-hint">
          Egyedi vásárlás (min. 100 kredit) a csomagok árazását követi. Kereskedői havi előfizetés: fix Ft/kredit (fent).
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <NumBox testid="admin-listing-upload" label="Feladási díj" suffix="kredit" value={cfg.listing_upload} onChange={(v) => setCfg({ ...cfg, listing_upload: v })} />
        <NumBox testid="admin-listing-daily" label="Napi fenntartás" suffix="kredit / nap" value={cfg.listing_daily} onChange={(v) => setCfg({ ...cfg, listing_daily: v })} />
        <NumBox testid="admin-detailed-stats-daily" label="Részletes statisztika" suffix="kredit / nap / autó (ha be van kapcsolva)" value={cfg.detailed_stats_daily} onChange={(v) => setCfg({ ...cfg, detailed_stats_daily: v })} />
      </div>

      <div className="glass rounded-2xl p-5" data-testid="admin-stats-guarantee">
        <h3 className="font-heading text-lg mb-1">Statisztika teljesítménygarancia</h3>
        <p className="text-xs text-slate-500 mb-4">
          Részletes stat bekapcsolása óta számított napok után, egyszer / autó: ha senki nem érdeklődött{" "}
          <b>VAGY</b> kevesebb megtekintés volt a küszöbnél → kredit jóváírás (napi cron).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <NumBox testid="admin-guarantee-credits" label="Jóváírás" suffix="kredit" value={cfg.detailed_stats_guarantee_credits ?? 60} onChange={(v) => setCfg({ ...cfg, detailed_stats_guarantee_credits: v })} />
          <NumBox testid="admin-guarantee-days" label="Várakozás" suffix="nap a bekapcsolástól" value={cfg.detailed_stats_guarantee_days ?? 14} onChange={(v) => setCfg({ ...cfg, detailed_stats_guarantee_days: v })} />
          <NumBox testid="admin-guarantee-max-views" label="Megtekintés küszöb" suffix="fölötte nincs garancia (ha van érdeklődés)" value={cfg.detailed_stats_guarantee_max_views ?? 10} onChange={(v) => setCfg({ ...cfg, detailed_stats_guarantee_max_views: v })} />
        </div>
      </div>

      <div className="glass rounded-2xl p-5" data-testid="admin-service-availability">
        <div className="flex items-center gap-2 mb-2">
          <ToggleLeft className="w-4 h-4 text-electric" strokeWidth={1.5} />
          <h3 className="font-heading text-lg">Szolgáltatások elérhetősége</h3>
        </div>
        <p className="text-xs text-slate-500 mb-4 max-w-2xl">
          Kikapcsolt szolgáltatást az eladók nem vehetnek igénybe (új hirdetés, kiegészítő aktiválás). A már aktív
          kiegészítők tovább futnak; csak az új vásárlás / bekapcsolás blokkolódik.
        </p>
        <div className="space-y-3">
          {SERVICE_AVAILABILITY_KEYS.map((key) => {
            const entry = cfg.service_availability?.[key] || { available: true, unavailable_message: "" };
            const off = entry.available === false;
            return (
              <div
                key={key}
                className="bg-white/[0.03] rounded-xl p-3 border border-white/5"
                data-testid={`admin-svc-avail-${key}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <label className="flex items-center gap-2 text-sm min-w-[200px] cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid={`admin-svc-avail-enabled-${key}`}
                      checked={entry.available !== false}
                      onChange={(e) => {
                        const service_availability = { ...cfg.service_availability };
                        service_availability[key] = {
                          ...entry,
                          available: e.target.checked,
                        };
                        setCfg({ ...cfg, service_availability });
                      }}
                      className="accent-[#2563EB]"
                    />
                    <span>{SERVICE_AVAILABILITY_LABELS[key] || key}</span>
                  </label>
                  {off && (
                    <input
                      type="text"
                      data-testid={`admin-svc-avail-msg-${key}`}
                      value={entry.unavailable_message || ""}
                      onChange={(e) => {
                        const service_availability = { ...cfg.service_availability };
                        service_availability[key] = {
                          ...entry,
                          unavailable_message: e.target.value,
                        };
                        setCfg({ ...cfg, service_availability });
                      }}
                      placeholder="Üzenet az eladóknak (opcionális)"
                      className="flex-1 bg-anthracite-900 border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-electric outline-none"
                    />
                  )}
                  {off && (
                    <span className="text-[10px] uppercase tracking-wider text-amber-400/90 shrink-0">Nem elérhető</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Section title="Egyszeri kiegészítők (kredit)" data={cfg.addons_one_time} onChange={(v) => setCfg({ ...cfg, addons_one_time: v })} testidPrefix="admin-onetime" labels={{ viewer_360: "360° körbeforgatás", ai_photo_bg: "AI háttércsere", ai_customer_support: "AI ügyfélszolgálat", facebook_crosspost: "Facebook Marketplace + csoportok" }} />
      <Section title="Heti kiegészítők (kredit / hét)" data={cfg.addons_weekly} onChange={(v) => setCfg({ ...cfg, addons_weekly: v })} testidPrefix="admin-weekly" labels={{ list_boost: "Kiemelés listában", priority_ranking: "Kiemelt rangsorolás", meta_ads: "Meta hirdetés" }} />
      <Section title="Kreditjutalmak" data={cfg.rewards} onChange={(v) => setCfg({ ...cfg, rewards: v })} testidPrefix="admin-reward" labels={{ signup: "Regisztráció", review_5star: "5★ vélemény", review_4star: "4★ vélemény", review_lower: "≤3★ vélemény", referral: "Referral bónusz", referral_threshold: "Ajánlások / bónusz (db)" }} />

      <div className="glass rounded-2xl p-5" data-testid="admin-listing-requirements">
        <div className="flex items-center gap-2 mb-4">
          <ClipboardList className="w-4 h-4 text-electric" strokeWidth={1.5} />
          <h3 className="font-heading text-lg">Hirdetésfeltöltés követelményei</h3>
        </div>
        <label
          className="flex items-start gap-3 mb-4 p-3 rounded-xl bg-white/[0.03] border border-white/5 cursor-pointer"
          data-testid="admin-listing-vin-required"
        >
          <input
            type="checkbox"
            className="mt-1 accent-[#2563EB]"
            checked={!!cfg.listing_vin_required}
            onChange={(e) => setCfg({ ...cfg, listing_vin_required: e.target.checked })}
          />
          <span className="text-sm">
            <span className="text-slate-200 font-medium">Alvázszám (VIN) kötelező új hirdetésnél</span>
            <span className="block text-xs text-slate-500 mt-1">
              Kikapcsolva: eladók link importtal / hasznaltauto.hu-val is feladhatnak VIN nélkül. Bekapcsolva: 17 karakteres érvényes VIN kell mentés előtt.
            </span>
          </span>
        </label>
        <p className="text-xs text-slate-500 mb-4">
          Ezek jelennek meg az eladóknak feltöltéskor — pipálniuk kell a kötelezőket a feladás előtt.
        </p>
        <div className="space-y-3">
          {(cfg.listing_requirements || []).map((req, i) => (
            <div key={req.id || i} className="flex flex-col sm:flex-row gap-2 bg-white/[0.03] rounded-xl p-3">
              <textarea
                data-testid={`admin-req-text-${i}`}
                rows={2}
                value={req.text}
                onChange={(e) => {
                  const listing_requirements = [...(cfg.listing_requirements || [])];
                  listing_requirements[i] = { ...listing_requirements[i], text: e.target.value };
                  setCfg({ ...cfg, listing_requirements });
                }}
                placeholder="Pl. Legalább 3 fotó különböző szögből…"
                className="flex-1 bg-anthracite-900 border border-white/10 rounded-lg p-2 text-sm focus:border-electric outline-none resize-none"
              />
              <div className="flex sm:flex-col items-center sm:items-stretch gap-2 shrink-0">
                <label className="flex items-center gap-2 text-xs text-slate-400 whitespace-nowrap px-2">
                  <input
                    type="checkbox"
                    data-testid={`admin-req-required-${i}`}
                    checked={!!req.required}
                    onChange={(e) => {
                      const listing_requirements = [...(cfg.listing_requirements || [])];
                      listing_requirements[i] = { ...listing_requirements[i], required: e.target.checked };
                      setCfg({ ...cfg, listing_requirements });
                    }}
                    className="accent-[#2563EB]"
                  />
                  Kötelező
                </label>
                <button
                  type="button"
                  data-testid={`admin-req-remove-${i}`}
                  onClick={() => setCfg({
                    ...cfg,
                    listing_requirements: (cfg.listing_requirements || []).filter((_, x) => x !== i),
                  })}
                  className="inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20"
                >
                  <Trash2 className="w-3 h-3" /> Törlés
                </button>
              </div>
            </div>
          ))}
        </div>
        <button
          type="button"
          data-testid="admin-req-add"
          onClick={() => setCfg({
            ...cfg,
            listing_requirements: [
              ...(cfg.listing_requirements || []),
              { id: `req_${Date.now()}`, text: "", required: true },
            ],
          })}
          className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full glass hover:bg-white/10"
        >
          <Plus className="w-3.5 h-3.5" /> Új követelmény
        </button>
      </div>

      <div>
        <button
          data-testid="admin-pricing-save"
          disabled={saving}
          onClick={save}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-electric hover:bg-electric-light disabled:opacity-50 text-white font-medium transition-all"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Változtatások mentése
        </button>
        <p className="text-xs text-slate-500 mt-2">A módosítások azonnal érvénybe lépnek a teljes oldalon.</p>
      </div>
    </div>
  );
}

const NumInput = ({ testid, label, value, onChange }) => (
  <div className="flex-1">
    <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">{label}</div>
    <input
      data-testid={testid}
      type="number"
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full bg-anthracite-900 border border-white/10 rounded p-1.5 text-sm focus:border-electric outline-none"
    />
  </div>
);

const NumBox = ({ testid, label, suffix, value, onChange }) => (
  <div className="glass rounded-xl p-3">
    <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">{label}</div>
    <div className="flex items-center gap-2">
      <input
        data-testid={testid}
        type="number"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-24 bg-anthracite-900 border border-white/10 rounded p-2 text-sm focus:border-electric outline-none"
      />
      <span className="text-xs text-slate-500">{suffix}</span>
    </div>
  </div>
);

const Section = ({ title, data, onChange, testidPrefix, labels }) => (
  <div className="glass rounded-2xl p-5">
    <h3 className="font-heading text-lg mb-3">{title}</h3>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      {Object.entries(data).map(([k, v]) => (
        <div key={k} className="bg-white/[0.03] rounded-lg p-3">
          <div className="text-xs text-slate-400 mb-1.5">{labels[k] || k}</div>
          <input
            data-testid={`${testidPrefix}-${k}`}
            type="number"
            value={v}
            onChange={(e) => onChange({ ...data, [k]: Number(e.target.value) })}
            className="w-full bg-anthracite-900 border border-white/10 rounded p-2 text-sm focus:border-electric outline-none"
          />
        </div>
      ))}
    </div>
  </div>
);
